import type { APIIntervals, APIOrderings } from '~/types/api'

export const formatThousands = (
  number: number,
  showDecimals?: boolean,
): string => {
  let roundedNumber = Math.round(number)
  let thousand = ''

  const thousands: Record<string, number> = {
    B: 9,
    M: 6,
    K: 3,
  }

  Object.entries(thousands).forEach(([key, value]) => {
    if (roundedNumber >= 10 ** value) {
      roundedNumber = Math.round((roundedNumber / 10 ** value) * 10) / 10
      thousand = key
    }
  })

  let result = `${roundedNumber}${thousand}`

  if (showDecimals && !thousand) {
    const decimals = number.toString().split('.')[1]?.slice(0, 2)
    if (decimals)
      result += `.${decimals}`
  }

  return result
}

export const formatPlural = (word: string, number?: number, showNumber = true) =>
  `${showNumber ? `${number} ` : ''}${word}${number !== 1 ? 's' : ''}`

export const formatTimeAgo = (date: Date) => {
  const intervals: Record<string, number> = {
    year: 31536000,
    month: 2592000,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1,
  }

  let counter = 0

  for (const [key, value] of Object.entries(intervals)) {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)

    counter = Math.floor(seconds / value)

    if (counter > 0)
      return `${formatPlural(key, counter)} ago`
  }

  return 'just now'
}

const padLeft = (value: number) => {
  return value < 10 ? `0${value}` : value
}

export const formatUSDate = (date: Date) => {
  return `${padLeft(date.getMonth() + 1)}/${padLeft(date.getDate())}/${date.getFullYear()}`
}

export const formatInterval = (interval: null | APIIntervals) => {
  const intervals: Record<APIIntervals, string> = {
    0: 'All Time',
    1: 'Today',
    7: 'This Week',
    30: 'This Month',
  }

  return interval ? intervals[interval] : 'All Time'
}

export const formatOrdering = (order: APIOrderings) => {
  const orderings: Record<APIOrderings, string> = {
    votes: 'Upvotes',
    uses: 'Most Uses',
    saves: 'Most Saved',
    newest: 'Newest',
    name: 'Name',
    custom: 'Custom',
  }

  return orderings[order]
}

export const topicNameToUrl = (name: string) => {
  return name.toLowerCase().replaceAll(' ', '-')
}

interface ILimitStringOptions {
  maxCharacters?: number
  maxWords?: number
}
export const limitString = (
  string: string,
  limit: number | ILimitStringOptions = {},
) => {
  let final = string
  const words = string.split(' ')

  if (typeof limit === 'number') {
    final = string.slice(0, limit)
    return final
  }

  if (limit.maxWords)
    final = words.slice(0, limit.maxWords).join(' ')

  if (limit.maxCharacters && string.length > limit.maxCharacters)
    final = `${final.slice(0, limit.maxCharacters - 3)}...`

  return final
}

export const cropDescriptionContent = (value: string, limit = 120) => {
  let resultString = ''
  let points = 0
  let hasReachedLimit = false
  for (let index = 0; index < value.length; index++) {
    resultString += value[index]
    points += value[index] === '\n' ? 50 : 1
    if (points >= limit) {
      hasReachedLimit = true
      break
    }
  }

  return {
    content: resultString,
    hasReachedLimit,
  }
}

export const capitalizeFirstLetter = (string?: string) => {
  if (!string)
    return ''

  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const formatNumber = (num: number) => {
  return num.toFixed(2)
}

export const formatNumberCurrency = (num: number) => {
  return new Intl.NumberFormat().format(num)
}

export const formatNumberRounded = (num: number) => {
  return num >= 1000 ? formatThousands(num) : new Intl.NumberFormat().format(num)
}
